/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, ExternalLink, Quote } from "lucide-react";
import { ASSETS, CREDENTIALS, VENTURES, SITE, REICO_QUOTE } from "@/constants/site";

export default function About() {
    return (
        <div data-testid="about-page">
            {/* Hero */}
            <section className="bg-[#0A192F] text-white py-28 relative overflow-hidden">
                <div className="absolute inset-0 bg-grain opacity-10" aria-hidden />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <span className="overline">About</span>
                        <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight">
                            Meet <span className="italic text-[#D4AF37]">Reico S. Hopewell.</span>
                        </h1>
                        <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
                            Entrepreneur. CEO. Grant writer. Fundraiser.
                            Recovery leader. Community advocate. For nearly two
                            decades, Reico has dedicated his work to helping
                            individuals, families, organizations, and
                            communities overcome barriers, achieve recovery,
                            and build sustainable futures.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-2">
                            {CREDENTIALS.map((c) => (
                                <div
                                    key={c.label}
                                    className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-2 text-xs"
                                >
                                    <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
                                    <span className="font-bold tracking-wider">
                                        {c.label}
                                    </span>
                                    <span className="text-slate-400">
                                        {c.title}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative aspect-[4/5] max-w-sm mx-auto">
                            <div className="absolute -inset-3 border border-[#D4AF37]/60" />
                            <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] to-[#0A192F] overflow-hidden">
                                <img
                                    src={ASSETS.reicoHeadshot}
                                    alt="Reico S. Hopewell"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Story */}
            <section className="py-28">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
                    <span className="overline">The Story</span>
                    <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] leading-tight">
                        From adversity to achievement.
                        <br />
                        From surviving to thriving.
                    </h2>
                    <div className="mt-10 space-y-6 text-lg text-slate-700 leading-relaxed">
                        <p>
                            Reico's path didn't start in a boardroom. It
                            started in the work — counseling clients, building
                            recovery housing, writing the grants that funded
                            real programs, and learning how to take an idea
                            from a napkin to a 501(c)(3), an LLC, a city
                            permit, or a signed contract.
                        </p>
                        <figure
                            data-testid="about-grants-photo"
                            className="my-6 rounded-sm overflow-hidden border border-slate-200 shadow-sm bg-slate-100"
                        >
                            <div className="aspect-[16/9] w-full overflow-hidden flex items-center justify-center">
                                <img
                                    src="https://dl.dropboxusercontent.com/scl/fi/wr6mg0aplz4s05jmm5eeu/IMG_6021.JPG?rlkey=eaekxei4e3at8sfew18hwo935&st=um43200j&dl=0"
                                    alt="Reico Hopewell facilitating a community workshop on grant writing and program funding"
                                    loading="lazy"
                                    className="w-full h-full object-cover object-center"
                                />
                            </div>
                            <figcaption className="px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 border-t border-slate-100">
                                Workshop · Grants, Funding & Program Strategy
                            </figcaption>
                        </figure>
                        <p>
                            That experience is the foundation of Hopewell
                            Consulting. Every engagement is informed by what
                            it actually takes to launch, fund, and operate an
                            organization in a city like Knoxville — and how to
                            do it with integrity.
                        </p>
                        <p>
                            <strong className="text-[#0A192F]">Lived experience.</strong>{" "}
                            <strong className="text-[#0A192F]">Professional expertise.</strong>{" "}
                            <strong className="text-[#0A192F]">Faith.</strong>{" "}
                            <strong className="text-[#0A192F]">Servant leadership.</strong>
                        </p>
                    </div>
                </div>
            </section>

            {/* Ventures */}
            <section className="bg-slate-50 py-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="mb-16 max-w-3xl">
                        <span className="overline">Ventures · Roles</span>
                        <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] leading-tight">
                            One leader. Five organizations. One mission.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {VENTURES.map((v, idx) => (
                            <div key={v.name} className="exec-card p-8 flex flex-col">
                                <span className="num-tag">0{idx + 1}</span>
                                <h3 className="font-serif text-2xl font-bold mt-4 text-[#0A192F]">
                                    {v.name}
                                </h3>
                                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                                    {v.role}
                                </p>
                                <p className="mt-4 text-slate-700 leading-relaxed text-sm flex-1">
                                    {v.blurb}
                                </p>
                                {v.url && (
                                    <a
                                        href={v.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`venture-link-${idx}`}
                                        className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#0A192F] hover:text-[#D4AF37] transition-colors"
                                    >
                                        Visit site <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Reico Quote */}
            <section className="bg-[#0A192F] text-white py-24">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <Quote className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
                    <blockquote className="font-serif text-2xl sm:text-3xl leading-snug">
                        {REICO_QUOTE.text}
                    </blockquote>
                    <p className="mt-8 text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold">
                        — {REICO_QUOTE.author}
                    </p>
                </div>
            </section>

            {/* Recognition */}
            <section className="py-28">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-4">
                        <span className="overline">Service · Recognition</span>
                        <h2 className="font-serif text-4xl font-bold mt-4 text-[#0A192F] leading-tight">
                            Board service & community leadership.
                        </h2>
                        <div className="mt-8 bg-[#0A192F] text-white p-6 rounded-sm border-l-4 border-[#D4AF37]">
                            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                                Active Appointment
                            </p>
                            <p className="mt-2 font-serif text-lg leading-snug font-bold">
                                E.A.P. / S.A.P. Provider
                            </p>
                            <p className="text-sm text-slate-300 mt-1">
                                Knoxville Utility Board (KUB)
                            </p>
                        </div>
                    </div>
                    <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-slate-700">
                        {[
                            "Board of Directors — E.M. Jellinek Center",
                            "Board of Directors — Mental Health Association of East Tennessee",
                            "Board of Directors — Boys & Girls Club of the Tennessee Valley",
                            "Advisory Board — Knoxville Preparatory School",
                            "Emeritus Board of Directors — Trinity Health Foundation",
                            "Knox County District Attorney's Citizens Academy",
                            "2021 Class of Leadership Knoxville",
                            "Police Advisory Review Committee (PARC) — 2021–2023",
                            "Metro Drug Coalition — Vice Chair & Chair",
                            "Governor Bill Lee's Volunteer Mentorship Initiative — 2021–Present",
                        ].map((row) => (
                            <li
                                key={row}
                                className="flex items-start gap-3 border-b border-slate-200 pb-4"
                            >
                                <span className="w-1.5 h-1.5 bg-[#D4AF37] mt-2 flex-shrink-0" />
                                {row}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Awards & Honors */}
            <section className="bg-slate-50 py-28 border-y border-slate-200">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="max-w-3xl mb-14">
                        <span className="overline">Awards · Honors</span>
                        <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] leading-tight">
                            Recognized for community impact.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {[
                            { title: "Community Champion Award", org: "Metro Drug Coalition" },
                            { title: "2022 Change Leader Award", org: "United Way of Greater Knoxville" },
                            { title: "2022 Minority-Owned Business of the Year", org: "U.S. Cellular & Pinnacle Bank" },
                            { title: "OBC Heroes Award", org: "Overcomer Believers Church" },
                            { title: "Outstanding Non-Profit Organization", org: "100 Women Who Care" },
                            { title: "Agent of Hope", org: "The Hope Dealers" },
                        ].map((a, idx) => (
                            <div
                                key={a.title}
                                data-testid={`award-${idx}`}
                                className="bg-white p-8"
                            >
                                <span className="num-tag">
                                    {String(idx + 1).padStart(2, "0")}
                                </span>
                                <h3 className="font-serif text-xl font-bold mt-4 text-[#0A192F] leading-tight">
                                    {a.title}
                                </h3>
                                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                                    {a.org}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0A192F] text-white py-20">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl font-bold">
                        Ready to build your next move?
                    </h3>
                    <p className="mt-4 text-slate-300">
                        Call {SITE.phone} or schedule a consultation.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                        <Link
                            to="/contact"
                            data-testid="about-cta-contact"
                            className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#C5A059] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2"
                        >
                            Schedule a Consultation <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
